import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CourseMaterial, UpdateCourseMaterialDto } from '../interfaces/course-material';
import {
  ApiCollectionResponse,
  ApiSingleResponse,
  ApiSimpleResponse,
} from '../interfaces/responses/api-response';
import { AcademicSchedulingOptions } from '../interfaces/academic-appointment';

/** Filtres de recherche de la bibliothèque. Tous facultatifs et cumulables. */
export interface CourseMaterialFilters {
  professorId?: number | null;
  learningPathId?: number | null;
  subjectId?: number | null;
  themeId?: number | null;
  groupId?: number | null;
  /** Valeur d'enum de catégorie (1=PDF … 99=Autre). */
  category?: number | null;
  search?: string | null;
  includeArchived?: boolean;
}

/**
 * Admin endpoints for managing course materials across the platform.
 *
 * GET    /api/admin/course-materials            (recherche filtrée)
 * GET    /api/admin/course-materials/{id}
 * PUT    /api/admin/course-materials/{id}
 * DELETE /api/admin/course-materials/{id}
 * GET    /api/admin/course-materials/{id}/download
 * GET    /api/admin/course-materials/by-learning-path/{id}
 * GET    /api/admin/course-materials/by-professor/{id}
 * GET    /api/admin/course-materials/by-subject/{id}
 * GET    /api/admin/course-materials/by-theme/{id}
 * GET    /api/admin/course-materials/options      (listes des filtres)
 * POST   /api/admin/course-materials/professor/{professorId}  (upload for a prof)
 */
@Injectable({ providedIn: 'root' })
export class AdminCourseMaterialService {
  private readonly base = `${environment.BASE_URL}/admin/course-materials`;

  constructor(private http: HttpClient) {}

  /**
   * Recherche transverse. Chaque filtre omis ne restreint rien : sans filtre,
   * l'appel renvoie toute la bibliothèque non archivée.
   */
  search(filters: CourseMaterialFilters = {}): Observable<ApiCollectionResponse<CourseMaterial>> {
    let params = new HttpParams();
    if (filters.professorId) params = params.set('professorId', filters.professorId);
    if (filters.learningPathId) params = params.set('learningPathId', filters.learningPathId);
    if (filters.subjectId) params = params.set('subjectId', filters.subjectId);
    if (filters.themeId) params = params.set('themeId', filters.themeId);
    if (filters.groupId) params = params.set('groupId', filters.groupId);
    if (filters.category) params = params.set('category', filters.category);
    if (filters.search?.trim()) params = params.set('search', filters.search.trim());
    if (filters.includeArchived) params = params.set('includeArchived', true);

    return this.http.get<ApiCollectionResponse<CourseMaterial>>(this.base, { params });
  }

  /**
   * Professeurs, matières et groupes des filtres.
   *
   * <p>Gardé par la même permission que l'écran (`library.read.all`). L'écran
   * les prenait au référentiel de planification, réservé au superviseur : pour
   * un inspecteur, l'appel répondait 403 et l'intercepteur le renvoyait sur
   * `/unauthorized`, alors qu'il avait le droit de lire la bibliothèque.</p>
   */
  options(): Observable<ApiSingleResponse<AcademicSchedulingOptions>> {
    return this.http.get<ApiSingleResponse<AcademicSchedulingOptions>>(`${this.base}/options`);
  }

  getById(id: number): Observable<ApiSingleResponse<CourseMaterial>> {
    return this.http.get<ApiSingleResponse<CourseMaterial>>(`${this.base}/${id}`);
  }

  updateMeta(
    id: number,
    dto: UpdateCourseMaterialDto,
  ): Observable<ApiSingleResponse<CourseMaterial>> {
    return this.http.put<ApiSingleResponse<CourseMaterial>>(`${this.base}/${id}`, dto);
  }

  delete(id: number): Observable<ApiSimpleResponse> {
    return this.http.delete<ApiSimpleResponse>(`${this.base}/${id}`);
  }

  /**
   * Remet un support archive en circulation.
   *
   * <p>L'archivage etait sans retour : une piece rangee par erreur ne se
   * recuperait qu'en base. Le geste est journalise cote serveur — remettre en
   * circulation un support retire engage. Il ne retablit pas la diffusion :
   * le support retrouve l'etat de publication qu'il avait.</p>
   */
  unarchive(id: number): Observable<ApiSimpleResponse> {
    return this.http.patch<ApiSimpleResponse>(`${this.base}/${id}/unarchive`, {});
  }

  download(id: number): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.base}/${id}/download`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  byLearningPath(pathId: number): Observable<ApiCollectionResponse<CourseMaterial>> {
    return this.http.get<ApiCollectionResponse<CourseMaterial>>(
      `${this.base}/by-learning-path/${pathId}`,
    );
  }

  byProfessor(professorId: number): Observable<ApiCollectionResponse<CourseMaterial>> {
    return this.http.get<ApiCollectionResponse<CourseMaterial>>(
      `${this.base}/by-professor/${professorId}`,
    );
  }

  bySubject(subjectId: number): Observable<ApiCollectionResponse<CourseMaterial>> {
    return this.http.get<ApiCollectionResponse<CourseMaterial>>(
      `${this.base}/by-subject/${subjectId}`,
    );
  }

  byTheme(themeId: number): Observable<ApiCollectionResponse<CourseMaterial>> {
    return this.http.get<ApiCollectionResponse<CourseMaterial>>(
      `${this.base}/by-theme/${themeId}`,
    );
  }

  /** Upload a material on behalf of a professor. */
  uploadForProfessor(
    professorId: number,
    formData: FormData,
  ): Observable<ApiSingleResponse<CourseMaterial>> {
    return this.http.post<ApiSingleResponse<CourseMaterial>>(
      `${this.base}/professor/${professorId}`,
      formData,
    );
  }
}
